import { useEffect, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import { shallowEqual } from "react-redux";
import { INACTIVE_TOKEN_COLOR } from "../../annotation/constants";
import { tintOnWhite } from "../../common/utilities/color";
import { HighlightedEntity } from "../../annotation/interfaces/annotation";
import { GroupBlockEntityType } from "../../configMap";
import { setHighlightedEntities } from "../../annotation/store/annotationSlice";

/**
 * Hook that manages annotation highlighting purely through DOM style manipulation.
 * This avoids triggering React re-renders when highlight state changes.
 * Uses MutationObserver to handle dynamically added annotations (virtualized pages).
 */
const useAnnotationHighlighting = () => {
  const dispatch = useAppDispatch();

  const highlightedEntities = useAppSelector(
    (state) => state.annotationReducer.highlightedEntities,
    shallowEqual,
  );

  const activeEntity = useAppSelector(
    (state) => state.appReducer.activeEntity,
    shallowEqual,
  );

  // Keep refs to current values for use in MutationObserver callback
  const highlightedEntitiesRef =
    useRef<HighlightedEntity[]>(highlightedEntities);
  const activeEntityRef = useRef<GroupBlockEntityType | undefined>(
    activeEntity,
  );

  // Update refs when values change
  useEffect(() => {
    highlightedEntitiesRef.current = highlightedEntities;
  }, [highlightedEntities]);

  useEffect(() => {
    activeEntityRef.current = activeEntity;
  }, [activeEntity]);

  // Clear highlighted entities when an entity becomes active
  useEffect(() => {
    if (activeEntity && highlightedEntities?.length) {
      dispatch(setHighlightedEntities([]));
    }
  }, [activeEntity, dispatch, highlightedEntities?.length]);

  // Function to apply highlighting to a single container
  const applyHighlightingToContainer = useCallback((container: Element) => {
    const highlighted = highlightedEntitiesRef.current;
    const active = activeEntityRef.current;

    const entityId = container.getAttribute("data-entity-id");
    const annotationIndex = container.getAttribute("data-annotation-index");
    const annotationItems = container.querySelectorAll(
      ".annotation-layer__item",
    );

    if (!entityId || !annotationItems.length) return;

    const index = annotationIndex ? parseInt(annotationIndex, 10) : null;

    // Check if this annotation should be highlighted
    const isHighlighted = highlighted?.some(
      (he) => he.id === entityId && (index === null || he.index === index),
    );

    // Check if this specific annotation is the active one
    const isActive =
      active?.entityType?.id === entityId &&
      (active?.index === undefined || active?.index === index);

    const shouldHighlight = isHighlighted || isActive;
    const hasHighlightedOrActive = highlighted?.length || active;

    annotationItems.forEach((item) => {
      const htmlItem = item as HTMLElement;
      const originalColor =
        htmlItem.style.getPropertyValue("--annotation-color");

      if (!hasHighlightedOrActive || shouldHighlight) {
        // Use original color
        if (originalColor) {
          htmlItem.style.backgroundColor = tintOnWhite(originalColor, 40);
          htmlItem.style.outline = `2px solid ${tintOnWhite(originalColor, 50)}`;
        }
        htmlItem.style.pointerEvents = "";
        // The grey tint is opaque, so a smaller grey mark painted later would
        // otherwise cover the highlighted one it sits inside.
        htmlItem.style.zIndex = hasHighlightedOrActive ? "17" : "";
      } else {
        // A greyed mark on top of the active one must not open its own menu.
        htmlItem.style.backgroundColor = tintOnWhite(INACTIVE_TOKEN_COLOR, 40);
        htmlItem.style.outline = `2px solid ${tintOnWhite(INACTIVE_TOKEN_COLOR, 50)}`;
        htmlItem.style.pointerEvents = "none";
        htmlItem.style.zIndex = "";
      }
    });
  }, []);

  // Function to apply highlighting to all containers
  const applyHighlightingToAll = useCallback(() => {
    const allContainers = document.querySelectorAll(
      ".annotation-layer__item-container",
    );
    allContainers.forEach(applyHighlightingToContainer);
  }, [applyHighlightingToContainer]);

  // Apply highlighting when state changes
  useEffect(() => {
    applyHighlightingToAll();
  }, [highlightedEntities, activeEntity, applyHighlightingToAll]);

  // Set up MutationObserver to handle dynamically added annotations
  useEffect(() => {
    const pagesContainer = document.getElementById("annotator-pages-container");
    if (!pagesContainer) return;

    const pendingContainers: Set<Element> = new Set();
    let rafId: number | null = null;

    const processPendingContainers = () => {
      pendingContainers.forEach(applyHighlightingToContainer);
      pendingContainers.clear();
      rafId = null;
    };

    const scheduleProcessing = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(processPendingContainers);
      }
    };

    const observer = new MutationObserver((mutations) => {
      // Only process if there are highlighted entities or active entity
      if (!highlightedEntitiesRef.current?.length && !activeEntityRef.current) {
        return;
      }

      // Collect all new annotation containers
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) {
            if (node.classList.contains("annotation-layer__item-container")) {
              pendingContainers.add(node);
            } else {
              const containers = node.getElementsByClassName(
                "annotation-layer__item-container",
              );
              for (const container of containers) {
                pendingContainers.add(container);
              }
            }
          }
        }
      }

      // Batch processing with requestAnimationFrame
      if (pendingContainers.size > 0) {
        scheduleProcessing();
      }
    });

    observer.observe(pagesContainer, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [applyHighlightingToContainer]);
};

export default useAnnotationHighlighting;
